import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { toast } from "react-toastify";

const InfiniteScrollCardView = ({
  columns = [],
  fetchSize = 20,
  apiUrl,
  apiParams,
  setState,
  enabled = false,
  setEnabled = () => {},
  refresh = false,
  sessionDependent = false,
  selectedSession = null,
  setTableData,
  renderCard,
  cardSx = {},
  ...props
}) => {
  const containerRef = useRef(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Infinite query to fetch paginated data
  const {
    data,
    fetchNextPage,
    isError,
    isFetching,
    isLoading,
    hasNextPage,
    refetch: refetchData,
    remove,
  } = useInfiniteQuery({
    queryKey: [
      "card-view-data",
      apiParams,
      enabled,
      sessionDependent && selectedSession,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiUrl({
        ...apiParams,
        page: pageParam,
        size: fetchSize,
      });
      return response?.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined;
    },
    onSuccess: (data) => {
      if (setTableData && typeof setTableData === "function") {
        setTableData(data.pages[0]);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message ?? "Error loading data");
    },
    refetchOnWindowFocus: false,
  });

  const flatData = React.useMemo(
    () =>
      data?.pages
        ? data.pages.flatMap((page) => page?.list ?? page?.template)
        : [],
    [data],
  );

  // Function to fetch more data when reaching the bottom
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement && hasNextPage) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 200 && !isFetching) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetching],
  );

  useEffect(() => {
    return () => {
      remove();
    };
  }, [remove]);

  useEffect(() => {
    if (refresh) {
      refetchData();
      setState((prev) => ({
        ...prev,
        refetchData: false,
      }));
    }
  }, [refresh, refetchData, setState]);

  useEffect(() => {
    const containerRefElement = containerRef.current;
    if (containerRefElement) {
      const onScroll = () => {
        fetchMoreOnBottomReached(containerRefElement);
      };
      containerRefElement.addEventListener("scroll", onScroll);
      return () => containerRefElement.removeEventListener("scroll", onScroll);
    }
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    if (setState) {
      setState((prev) => ({
        ...prev,
        tableData: flatData,
      }));
      setPage(data?.pages?.length ?? 1);
      setPages(data?.pages[0]?.pages ?? 1);
    }
  }, [data?.pages, flatData, setState]);

  // Default card renderer if not provided
  const defaultRenderCard = (item, index) => {
    // Separate columns into groups for better layout
    const regularColumns = [];
    const actionColumn = columns.find(
      (col) =>
        col.header === "Action" ||
        col.header === "Actions" ||
        col.accessorKey === "action",
    );

    columns.forEach((column) => {
      if (column.accessorKey === "#" || column.header === "#") return;
      if (
        column.header === "Action" ||
        column.header === "Actions" ||
        column.accessorKey === "action"
      )
        return;
      regularColumns.push(column);
    });

    return (
      <Card
        key={index}
        sx={{
          mb: 1.5,
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          boxShadow: "none",
          overflow: "hidden",
          backgroundColor: "#fff",
          ...cardSx,
        }}
      >
        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
          {/* Grid layout for fields - 2 columns */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
              mb: actionColumn ? 1.5 : 0,
            }}
          >
            {regularColumns.map((column, colIndex) => {
              let value;
              let displayValue;

              // Use custom Cell renderer if it exists
              if (column.Cell) {
                // Derive the cell value from accessorKey or accessorFn so
                // column renderers that call cell.getValue() work correctly.
                let cellValue;
                if (column.accessorFn) {
                  cellValue = column.accessorFn(item);
                } else if (column.accessorKey) {
                  const keys = column.accessorKey.split(".");
                  cellValue = keys.reduce((obj, key) => obj?.[key], item);
                }
                displayValue = column.Cell({
                  cell: {
                    getValue: () => cellValue,
                    row: {
                      original: item,
                      id: index,
                    },
                  },
                  row: {
                    original: item, 
                    id: index,
                  },
                });
              } else if (column.accessorFn) {
                value = column.accessorFn(item);
                displayValue = value;
              } else if (column.accessorKey) {
                const keys = column.accessorKey.split(".");
                value = keys.reduce((obj, key) => obj?.[key], item);
                displayValue = value;
              }

              return (
                <Box key={colIndex}>
                  <Typography
                    // sx={{
                    //   fontWeight: 400,
                    //   color: "#666",
                    //   fontSize: "11px",
                    //   mb: 0.3,
                    // }}
                    variant="button"
                    color="primary"
                  >
                    {column.header}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "12px",
                      color: "#000",
                      fontWeight: 600,
                      "& a": {
                        color: "#2789FD",
                        textDecoration: "none",
                        fontWeight: 500,
                      },
                      "& .MuiTypography-root": {
                        fontSize: "12px",
                        fontWeight: 500,
                      },
                    }}
                  >
                    {displayValue ?? "--"}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Action buttons at bottom if exists */}
          {actionColumn && (
            <Box
              sx={{
                pt: 1.5,
                borderTop: "0.5px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {actionColumn.Cell({
                cell: {
                  row: {
                    original: item,
                    id: index,
                  },
                },
                row: {
                  original: item,
                  id: index,
                },
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <Typography color="error">Error loading data</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Select All and Pagination Info */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          px: 1,
          py: 1,
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "#666" }}>
          Page {page} of {pages} • {flatData.length} items
        </Typography>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          maxHeight: "70vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "#999",
            },
          },
        }}
      >
        {flatData.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
              gap: 2,
            }}
          >
            <Typography sx={{ color: "#999", fontSize: "14px" }}>
              No data available
            </Typography>
          </Box>
        ) : (
          <>
            {flatData.map((item, index) => {
              return renderCard
                ? renderCard(item, index)
                : defaultRenderCard(item, index);
            })}

            {isFetching && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 3,
                  gap: 1,
                }}
              >
                <CircularProgress size={24} thickness={4} />
                <Typography variant="button" sx={{ color: "#666" }}>
                  Loading more...
                </Typography>
              </Box>
            )}

            {!hasNextPage && flatData.length > 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 1,
                  mt: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#999",
                    fontSize: "12px",
                    display: "inline-block",
                    px: 2,
                    py: 1,
                    borderRadius: "20px",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  All data loaded
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default InfiniteScrollCardView;
